/**
 * QuickLinksWidget — admin-configurable workspace links + user's personal links.
 * Backed by GET/POST/PATCH/DELETE /api/v1/workspaces/{ws}/quick-links.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link as LinkIcon, Plus, Trash2, Pencil, ExternalLink, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function QuickLinksWidget() {
  const { activeWorkspaceId, activeWorkspace, user, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const isAdmin = ["owner", "admin"].includes(_membership?.role)
    || ["owner", "admin"].includes(activeWorkspace?.role)
    || activeWorkspace?.owner_id === user?.id
    || can("workspace.manage");

  const [data, setData] = useState({ workspace: [], user: [] });
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // {scope, ...link} | null

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/quick-links`);
      setData(data || { workspace: [], user: [] });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load links")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const remove = async (link) => {
    if (!confirm(`Remove "${link.label}"?`)) return;
    try {
      await api.delete(`/quick-links/${link.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const wsLinks = data.workspace || [];
  const userLinks = data.user || [];

  return (
    <section className="border border-border" data-testid="quick-links-widget">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 bg-primary/5">
        <LinkIcon className="w-4 h-4" />
        <div className="label-eyebrow">Quick links</div>
        <button
          type="button"
          onClick={() => setEditing({ scope: "user" })}
          className="ml-auto text-xs underline hover:text-primary"
          data-testid="quick-link-add-btn"
        >
          + Add
        </button>
      </div>
      <div className="divide-y divide-border">
        {busy && !wsLinks.length && !userLinks.length && (
          <div className="px-4 py-4 text-xs text-muted-foreground">Loading…</div>
        )}

        {wsLinks.length > 0 && (
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Workspace
            </div>
            <ul className="space-y-1">
              {wsLinks.map((l) => (
                <LinkRow key={l.id} link={l} isAdmin={isAdmin} onEdit={setEditing} onDelete={remove} />
              ))}
            </ul>
          </div>
        )}

        <div className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
            <User className="w-3 h-3" /> My links
          </div>
          {userLinks.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No personal links yet. Click &quot;+ Add&quot; to bookmark anything.</div>
          ) : (
            <ul className="space-y-1">
              {userLinks.map((l) => (
                <LinkRow key={l.id} link={l} isAdmin={true} onEdit={setEditing} onDelete={remove} />
              ))}
            </ul>
          )}
        </div>

        {isAdmin && (
          <div className="px-4 py-2 bg-muted/20">
            <button
              type="button"
              onClick={() => setEditing({ scope: "workspace" })}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="quick-link-add-workspace-btn"
            >
              <Plus className="w-3 h-3" /> Add workspace link (visible to everyone)
            </button>
          </div>
        )}
      </div>

      {editing && (
        <QuickLinkDialog
          link={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await load(); setEditing(null); }}
        />
      )}
    </section>
  );
}

function LinkRow({ link, isAdmin, onEdit, onDelete }) {
  const canEdit = link.scope === "user" ? true : isAdmin;
  return (
    <li className="group flex items-center gap-1.5 text-sm" data-testid={`quick-link-${link.id}`}>
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 min-w-0 truncate hover:text-primary hover:underline inline-flex items-center gap-1.5"
        data-testid={`quick-link-open-${link.id}`}
      >
        <span className="text-base leading-none">{link.icon || "🔗"}</span>
        <span className="truncate">{link.label}</span>
        <ExternalLink className="w-3 h-3 shrink-0 opacity-40" />
      </a>
      {canEdit && (
        <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-0.5">
          <button
            onClick={() => onEdit(link)}
            className="p-1 hover:bg-muted"
            title="Edit"
            data-testid={`quick-link-edit-${link.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(link)}
            className="p-1 hover:bg-red-500/10 text-red-600"
            title="Remove"
            data-testid={`quick-link-delete-${link.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </li>
  );
}

function QuickLinkDialog({ link, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !link?.id;
  const [form, setForm] = useState({
    label: link.label || "",
    url: link.url || "",
    icon: link.icon || "🔗",
    scope: link.scope || "user",
    order: link.order || 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.label.trim()) { toast.error("Label required"); return; }
    let url = form.url.trim();
    if (!url) { toast.error("URL required"); return; }
    if (!/^https?:\/\//i.test(url) && !url.startsWith("/")) url = `https://${url}`;
    setBusy(true);
    try {
      const payload = { ...form, url };
      if (isNew) await api.post(`/workspaces/${activeWorkspaceId}/quick-links`, payload);
      else await api.patch(`/quick-links/${link.id}`, payload);
      toast.success("Saved");
      await onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="quick-link-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add quick link" : "Edit quick link"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Label *</Label>
            <Input
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
              placeholder="e.g. Figma board"
              className="h-10 rounded-none"
              data-testid="quick-link-label-input"
              maxLength={60}
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">URL *</Label>
            <Input
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
              placeholder="https://…"
              className="h-10 rounded-none"
              data-testid="quick-link-url-input"
              maxLength={500}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Icon (emoji)</Label>
              <Input
                value={form.icon || ""}
                onChange={(e) => set("icon", e.target.value)}
                placeholder="🔗"
                className="h-10 rounded-none"
                data-testid="quick-link-icon-input"
                maxLength={4}
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Visibility</Label>
              <Select value={form.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="quick-link-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Only me</SelectItem>
                  <SelectItem value="workspace">Everyone (admin)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-none"
            data-testid="quick-link-save-btn"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
